// Client-side helpers for persisting + decoding the panellist JWT.
// Decoding here is informational only (UI shows the tier, exp); the
// authoritative verify happens server-side in every Edge Function call.

const STORAGE_KEY = (surveyId: string) => `clinpay:token:${surveyId}`

export interface DecodedToken {
  surveyId: string
  tier: string
  iss?: string
  iat?: number
  exp: number
  expired: boolean
}

function b64urlDecode(input: string): string {
  const pad = '='.repeat((4 - (input.length % 4)) % 4)
  const b64 = (input + pad).replace(/-/g, '+').replace(/_/g, '/')
  return atob(b64)
}

export function decodeToken(token: string): DecodedToken | null {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    const payload = JSON.parse(decodeURIComponent(escape(b64urlDecode(parts[1])))) as {
      surveyId?: string
      tier?: string
      iss?: string
      iat?: number
      exp?: number
    }
    if (!payload.surveyId || !payload.tier || typeof payload.exp !== 'number') return null
    const now = Math.floor(Date.now() / 1000)
    return {
      surveyId: payload.surveyId,
      tier: payload.tier,
      iss: payload.iss,
      iat: payload.iat,
      exp: payload.exp,
      expired: payload.exp < now,
    }
  } catch {
    return null
  }
}

export function rememberToken(surveyId: string, token: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY(surveyId), token)
  } catch {
    /* sessionStorage may be unavailable (private browsing) — fine */
  }
}

export function recallToken(surveyId: string): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY(surveyId))
  } catch {
    return null
  }
}

export function forgetToken(surveyId: string): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY(surveyId))
  } catch {
    /* noop */
  }
}

// Common loader: reads token from query string first (and persists it),
// falls back to sessionStorage. Used by every route under /p/:surveyId.
export function loadToken(surveyId: string, search: string): {
  token: string | null
  decoded: DecodedToken | null
  source: 'query' | 'session' | 'none'
} {
  const params = new URLSearchParams(search)
  const queryToken = params.get('token')
  if (queryToken) {
    rememberToken(surveyId, queryToken)
    return { token: queryToken, decoded: decodeToken(queryToken), source: 'query' }
  }
  const stored = recallToken(surveyId)
  if (stored) {
    return { token: stored, decoded: decodeToken(stored), source: 'session' }
  }
  return { token: null, decoded: null, source: 'none' }
}
